import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { getProjectGeoFencesMutationKey } from '.';
import { projectService } from '../../application/ProjectService';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { GeoFence } from '../../domain/geoFence/GeoFence';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUserCredentials } from '../user/useRetrieveUserCredentials';
import { useRetrieveActiveProject } from './useRetrieveActiveProject';

type UseRetrieveActiveProjectGeoFencesResponse = ServiceResponse<Function, GeoFence[]>;

// Hooks act as 'Adapter' layer.
export const useRetrieveActiveProjectGeoFences = (): UseRetrieveActiveProjectGeoFencesResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { projectState, projectDispatch } = useProjectContext();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: activeProject } = useRetrieveActiveProject();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(
    [getProjectGeoFencesMutationKey(), activeProject!.id],
    (): Promise<GeoFence[]> => projectService.fetchProjectGeoFences({ projectId: activeProject!.id }),
    {
      enabled: !!activeProject && userCredentials?.subscription.test(`planner.geofences`),
      retry: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      onSuccess: (geoFences) => {
        projectDispatch({
          type: ProjectActionType.FETCH_PROJECT_GEOFENCES_SUCCESS,
          payload: { geoFences },
        });
      },
      onError: (err: any) => {
        console.info(`Planner Retrieve ProjectBase GeoFence Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: projectState.geoFences,
  };
};
